/**
 * v3.15-1230 SMART-INTAKE-003 — Payment Methods tab (Client Detail).
 *
 * Presentation + management surface for payment methods captured during
 * Smart Client Intake (SMART-INTAKE-002).  Reads exclusively from the
 * existing collections:
 *   • client_credit_cards
 *   • client_ach_accounts
 *
 * Supports Edit / Set Default / Soft-Delete.  Never creates NEW cards
 * or accounts here — Smart Client Intake remains the sole creation
 * surface, matching the architecture directive.
 */
import React, { useCallback, useEffect, useState } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  CreditCard, Landmark, Pencil, Star, Trash2, Loader2,
  ShieldCheck, RefreshCw,
} from 'lucide-react';

const fmtCurrency = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); }
  catch { return String(iso); }
};

const last4 = (num) => {
  if (!num) return '—';
  const s = String(num).replace(/\s+/g, '');
  return s.length >= 4 ? `•••• ${s.slice(-4)}` : s;
};

const DefaultPill = ({ on }) => (
  on ? (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-semibold"
      data-testid="pm-default-pill"
    >
      <Star className="h-3 w-3" /> Default
    </span>
  ) : null
);

const StatusPill = ({ archived }) => (
  archived ? (
    <span className="inline-flex px-2 py-0.5 text-[10px] rounded-full bg-slate-100 text-slate-500 border border-slate-200">
      Archived
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
      <ShieldCheck className="h-3 w-3" /> Active
    </span>
  )
);


const PaymentMethodsTab = ({ clientId }) => {
  const [loading, setLoading] = useState(true);
  const [cards, setCards] = useState([]);
  const [achs, setAchs] = useState([]);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [editing, setEditing] = useState(null); // { kind:'cc'|'ach', item }

  const fetchAll = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const q = includeArchived ? '?include_archived=true' : '';
      const [ccRes, achRes] = await Promise.all([
        api.get(`/clients/${clientId}/credit-cards${q}`),
        api.get(`/clients/${clientId}/ach-accounts${q}`),
      ]);
      setCards(ccRes?.data?.items || []);
      setAchs(achRes?.data?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load payment methods');
    } finally {
      setLoading(false);
    }
  }, [clientId, includeArchived]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const setCcDefault = async (id) => {
    try {
      await api.post(`/clients/${clientId}/credit-cards/${id}/default`);
      toast.success('Default credit card updated');
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to set default');
    }
  };
  const setAchDefault = async (id) => {
    try {
      await api.post(`/clients/${clientId}/ach-accounts/${id}/default`);
      toast.success('Default ACH account updated');
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to set default');
    }
  };
  const softDeleteCc = async (id) => {
    if (!window.confirm('Archive this credit card? History will be preserved.')) return;
    try {
      await api.delete(`/clients/${clientId}/credit-cards/${id}`);
      toast.success('Credit card archived');
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to archive');
    }
  };
  const softDeleteAch = async (id) => {
    if (!window.confirm('Archive this ACH account? History will be preserved.')) return;
    try {
      await api.delete(`/clients/${clientId}/ach-accounts/${id}`);
      toast.success('ACH account archived');
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to archive');
    }
  };

  return (
    <div data-testid="payment-methods-tab" className="space-y-6 p-2">
      {/* Header controls */}
      <div className="flex items-center justify-between border-b pb-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-emerald-600" />
            Payment Methods
          </h3>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Read from <code className="text-[10px] bg-slate-100 px-1 rounded">client_credit_cards</code> and{' '}
            <code className="text-[10px] bg-slate-100 px-1 rounded">client_ach_accounts</code>.
            New payment methods are captured via Smart Client Intake.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <Checkbox
              checked={includeArchived}
              onCheckedChange={(v) => setIncludeArchived(!!v)}
              data-testid="pm-include-archived"
            />
            Show archived
          </label>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchAll}
            data-testid="pm-refresh-btn"
          >
            <RefreshCw className="h-3.5 w-3.5 mr-1" />
            Refresh
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-12 text-center text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
          <p className="text-xs">Loading payment methods…</p>
        </div>
      ) : (
        <>
          {/* ── Credit Cards ────────────────────────────────────────── */}
          <section data-testid="pm-cc-section">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-bold uppercase tracking-wide text-slate-600 flex items-center gap-2">
                <CreditCard className="h-3.5 w-3.5" /> Credit Cards ({cards.length})
              </h4>
            </div>
            {cards.length === 0 ? (
              <p className="text-xs italic text-slate-400 py-4 text-center border border-dashed rounded">
                No credit cards on file. Capture cards via Smart Client Intake.
              </p>
            ) : (
              <div className="grid gap-2">
                {cards.map((c) => (
                  <div
                    key={c.id}
                    data-testid={`pm-cc-row-${c.id}`}
                    className={`border rounded-md p-3 text-sm ${c.soft_deleted ? 'bg-slate-50 opacity-70' : 'bg-white'}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className="font-semibold text-slate-800">
                            {c.payment_nickname || c.bank_name || 'Credit Card'}
                          </span>
                          <DefaultPill on={c.is_default} />
                          <StatusPill archived={c.soft_deleted} />
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-xs text-slate-600">
                          <div><span className="text-slate-400">Cardholder:</span> {c.cardholder_name || '—'}</div>
                          <div><span className="text-slate-400">Bank:</span> {c.bank_name || '—'}</div>
                          <div><span className="text-slate-400">Last 4:</span> {last4(c.card_number)}</div>
                          <div><span className="text-slate-400">Brand:</span> {c.card_brand || '—'}</div>
                          <div><span className="text-slate-400">Limit:</span> {fmtCurrency(c.credit_limit)}</div>
                          <div><span className="text-slate-400">Balance:</span> {fmtCurrency(c.current_balance)}</div>
                          <div><span className="text-slate-400">Min Pymt:</span> {fmtCurrency(c.minimum_payment)}</div>
                          <div><span className="text-slate-400">APR:</span> {c.interest_rate != null && c.interest_rate !== '' ? `${c.interest_rate}%` : '—'}</div>
                          <div className="col-span-2"><span className="text-slate-400">Created:</span> {fmtDate(c.created_at)} {c.created_by && `by ${c.created_by}`}</div>
                          <div className="col-span-2"><span className="text-slate-400">Last Updated:</span> {fmtDate(c.updated_at)}</div>
                        </div>
                      </div>
                      {!c.soft_deleted && (
                        <div className="flex flex-col gap-1 shrink-0">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            onClick={() => setEditing({ kind: 'cc', item: c })}
                            data-testid={`pm-cc-edit-${c.id}`}
                          >
                            <Pencil className="h-3 w-3 mr-1" /> Edit
                          </Button>
                          {!c.is_default && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs"
                              onClick={() => setCcDefault(c.id)}
                              data-testid={`pm-cc-default-${c.id}`}
                            >
                              <Star className="h-3 w-3 mr-1" /> Set Default
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs text-rose-600 border-rose-200 hover:bg-rose-50"
                            onClick={() => softDeleteCc(c.id)}
                            data-testid={`pm-cc-delete-${c.id}`}
                          >
                            <Trash2 className="h-3 w-3 mr-1" /> Archive
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* ── ACH Accounts ────────────────────────────────────────── */}
          <section data-testid="pm-ach-section">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-bold uppercase tracking-wide text-slate-600 flex items-center gap-2">
                <Landmark className="h-3.5 w-3.5" /> ACH Accounts ({achs.length})
              </h4>
            </div>
            {achs.length === 0 ? (
              <p className="text-xs italic text-slate-400 py-4 text-center border border-dashed rounded">
                No ACH accounts on file. Capture accounts via Smart Client Intake.
              </p>
            ) : (
              <div className="grid gap-2">
                {achs.map((a) => (
                  <div
                    key={a.id}
                    data-testid={`pm-ach-row-${a.id}`}
                    className={`border rounded-md p-3 text-sm ${a.soft_deleted ? 'bg-slate-50 opacity-70' : 'bg-white'}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className="font-semibold text-slate-800">
                            {a.payment_nickname || a.bank_name || 'ACH Account'}
                          </span>
                          <DefaultPill on={a.is_default} />
                          <StatusPill archived={a.soft_deleted} />
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-xs text-slate-600">
                          <div><span className="text-slate-400">Holder:</span> {a.account_holder_name || '—'}</div>
                          <div><span className="text-slate-400">Bank:</span> {a.bank_name || '—'}</div>
                          <div><span className="text-slate-400">Type:</span> {a.account_type || '—'}</div>
                          <div><span className="text-slate-400">Auth:</span> {a.authorization_given ? 'Yes' : 'No'}</div>
                          <div><span className="text-slate-400">Routing:</span> {a.routing_number || '—'}</div>
                          <div className="col-span-2"><span className="text-slate-400">Account #:</span> {a.account_number || '—'}</div>
                          <div className="col-span-2"><span className="text-slate-400">Created:</span> {fmtDate(a.created_at)} {a.created_by && `by ${a.created_by}`}</div>
                          <div className="col-span-2"><span className="text-slate-400">Last Updated:</span> {fmtDate(a.updated_at)}</div>
                        </div>
                      </div>
                      {!a.soft_deleted && (
                        <div className="flex flex-col gap-1 shrink-0">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            onClick={() => setEditing({ kind: 'ach', item: a })}
                            data-testid={`pm-ach-edit-${a.id}`}
                          >
                            <Pencil className="h-3 w-3 mr-1" /> Edit
                          </Button>
                          {!a.is_default && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs"
                              onClick={() => setAchDefault(a.id)}
                              data-testid={`pm-ach-default-${a.id}`}
                            >
                              <Star className="h-3 w-3 mr-1" /> Set Default
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs text-rose-600 border-rose-200 hover:bg-rose-50"
                            onClick={() => softDeleteAch(a.id)}
                            data-testid={`pm-ach-delete-${a.id}`}
                          >
                            <Trash2 className="h-3 w-3 mr-1" /> Archive
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}

      {/* Edit dialog */}
      <PaymentMethodEditDialog
        clientId={clientId}
        editing={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); fetchAll(); }}
      />
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════
// Edit dialog — updates existing record in place; never creates new.
// ═══════════════════════════════════════════════════════════════════
const PaymentMethodEditDialog = ({ clientId, editing, onClose, onSaved }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing?.item) setForm({ ...editing.item });
    else setForm({});
  }, [editing]);

  if (!editing) return null;

  const isCc = editing.kind === 'cc';
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      if (isCc) {
        await api.put(`/clients/${clientId}/credit-cards/${form.id}`, {
          payment_nickname: form.payment_nickname || null,
          bank_name: form.bank_name || null,
          cardholder_name: form.cardholder_name || null,
          card_number: form.card_number || null,
          card_brand: form.card_brand || null,
          expiration_month: form.expiration_month || null,
          expiration_year: form.expiration_year || null,
          cvv: form.cvv || null,
          billing_zip: form.billing_zip || null,
          credit_limit: form.credit_limit !== '' && form.credit_limit != null ? parseFloat(form.credit_limit) : null,
          current_balance: form.current_balance !== '' && form.current_balance != null ? parseFloat(form.current_balance) : null,
          minimum_payment: form.minimum_payment !== '' && form.minimum_payment != null ? parseFloat(form.minimum_payment) : null,
          interest_rate: form.interest_rate !== '' && form.interest_rate != null ? parseFloat(form.interest_rate) : null,
          notes: form.notes || null,
        });
        toast.success('Credit card updated');
      } else {
        await api.put(`/clients/${clientId}/ach-accounts/${form.id}`, {
          payment_nickname: form.payment_nickname || null,
          account_holder_name: form.account_holder_name || null,
          bank_name: form.bank_name || null,
          routing_number: form.routing_number || null,
          account_number: form.account_number || null,
          account_type: form.account_type || null,
          authorization_given: !!form.authorization_given,
          notes: form.notes || null,
        });
        toast.success('ACH account updated');
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!editing} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="pm-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            {isCc ? <CreditCard className="h-4 w-4" /> : <Landmark className="h-4 w-4" />}
            Edit {isCc ? 'Credit Card' : 'ACH Account'}
          </DialogTitle>
        </DialogHeader>

        {isCc ? (
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Payment Nickname</Label>
              <Input value={form.payment_nickname || ''} onChange={(e) => setField('payment_nickname', e.target.value)} data-testid="pm-edit-nickname" /></div>
            <div><Label className="text-xs">Bank / Issuer</Label>
              <Input value={form.bank_name || ''} onChange={(e) => setField('bank_name', e.target.value)} data-testid="pm-edit-bank" /></div>
            <div><Label className="text-xs">Cardholder Name</Label>
              <Input value={form.cardholder_name || ''} onChange={(e) => setField('cardholder_name', e.target.value)} data-testid="pm-edit-cardholder" /></div>
            <div><Label className="text-xs">Card Brand</Label>
              <Select value={form.card_brand || ''} onValueChange={(v) => setField('card_brand', v)}>
                <SelectTrigger data-testid="pm-edit-brand"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="visa">Visa</SelectItem>
                  <SelectItem value="mastercard">MasterCard</SelectItem>
                  <SelectItem value="amex">American Express</SelectItem>
                  <SelectItem value="discover">Discover</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select></div>
            <div className="col-span-2"><Label className="text-xs">Card Number</Label>
              <Input value={form.card_number || ''} onChange={(e) => setField('card_number', e.target.value)} data-testid="pm-edit-number" /></div>
            <div><Label className="text-xs">Exp. Month</Label>
              <Input value={form.expiration_month || ''} onChange={(e) => setField('expiration_month', e.target.value)} /></div>
            <div><Label className="text-xs">Exp. Year</Label>
              <Input value={form.expiration_year || ''} onChange={(e) => setField('expiration_year', e.target.value)} /></div>
            <div><Label className="text-xs">CVV</Label>
              <Input value={form.cvv || ''} onChange={(e) => setField('cvv', e.target.value)} /></div>
            <div><Label className="text-xs">Billing Zip</Label>
              <Input value={form.billing_zip || ''} onChange={(e) => setField('billing_zip', e.target.value)} /></div>
            <div><Label className="text-xs">Credit Limit</Label>
              <Input type="number" value={form.credit_limit ?? ''} onChange={(e) => setField('credit_limit', e.target.value)} data-testid="pm-edit-limit" /></div>
            <div><Label className="text-xs">Current Balance</Label>
              <Input type="number" value={form.current_balance ?? ''} onChange={(e) => setField('current_balance', e.target.value)} /></div>
            <div><Label className="text-xs">Minimum Payment</Label>
              <Input type="number" value={form.minimum_payment ?? ''} onChange={(e) => setField('minimum_payment', e.target.value)} /></div>
            <div><Label className="text-xs">Interest Rate (APR %)</Label>
              <Input type="number" step="0.01" value={form.interest_rate ?? ''} onChange={(e) => setField('interest_rate', e.target.value)} /></div>
            <div className="col-span-2"><Label className="text-xs">Notes</Label>
              <Input value={form.notes || ''} onChange={(e) => setField('notes', e.target.value)} /></div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Payment Nickname</Label>
              <Input value={form.payment_nickname || ''} onChange={(e) => setField('payment_nickname', e.target.value)} data-testid="pm-edit-ach-nickname" /></div>
            <div><Label className="text-xs">Account Holder</Label>
              <Input value={form.account_holder_name || ''} onChange={(e) => setField('account_holder_name', e.target.value)} data-testid="pm-edit-ach-holder" /></div>
            <div><Label className="text-xs">Bank Name</Label>
              <Input value={form.bank_name || ''} onChange={(e) => setField('bank_name', e.target.value)} data-testid="pm-edit-ach-bank" /></div>
            <div><Label className="text-xs">Account Type</Label>
              <Select value={form.account_type || ''} onValueChange={(v) => setField('account_type', v)}>
                <SelectTrigger data-testid="pm-edit-ach-type"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="checking">Checking</SelectItem>
                  <SelectItem value="savings">Savings</SelectItem>
                </SelectContent>
              </Select></div>
            <div><Label className="text-xs">Routing Number</Label>
              <Input value={form.routing_number || ''} onChange={(e) => setField('routing_number', e.target.value)} data-testid="pm-edit-ach-routing" /></div>
            <div><Label className="text-xs">Account Number</Label>
              <Input value={form.account_number || ''} onChange={(e) => setField('account_number', e.target.value)} data-testid="pm-edit-ach-account" /></div>
            <div className="col-span-2 flex items-center gap-2 pt-1">
              <Checkbox
                checked={!!form.authorization_given}
                onCheckedChange={(v) => setField('authorization_given', !!v)}
                data-testid="pm-edit-ach-auth"
              />
              <Label className="text-xs cursor-pointer">Client has authorized ACH debit from this account</Label>
            </div>
            <div className="col-span-2"><Label className="text-xs">Notes</Label>
              <Input value={form.notes || ''} onChange={(e) => setField('notes', e.target.value)} /></div>
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} data-testid="pm-edit-cancel">Cancel</Button>
          <Button type="button" onClick={save} disabled={saving} data-testid="pm-edit-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


export default PaymentMethodsTab;
